export const SESSION_INSTRUCTOR_ROLES = ["lead", "assist"] as const;
export type SessionInstructorRole = (typeof SESSION_INSTRUCTOR_ROLES)[number];

export type SessionInstructorAssignment = {
  instructorId: string;
  role: SessionInstructorRole;
};

export class SessionInstructorsValidationError extends Error {
  readonly code = "SESSION_INSTRUCTORS_INVALID";
  constructor(message: string) {
    super(message);
    this.name = "SessionInstructorsValidationError";
  }
}

/**
 * Normalize create/update payload to role assignments.
 * Legacy `instructorIds`: first = lead, rest = assist (max 3).
 */
export function normalizeAndValidateSessionInstructors(input: {
  instructors?: SessionInstructorAssignment[] | null;
  instructorIds?: string[] | null;
  engagementKind: "standard" | "partner_instructor_only";
}): SessionInstructorAssignment[] {
  let list: SessionInstructorAssignment[];

  if (input.instructors != null && input.instructors.length > 0) {
    list = input.instructors.map((r) => ({
      instructorId: r.instructorId,
      role: r.role,
    }));
  } else if (input.instructorIds != null && input.instructorIds.length > 0) {
    const uniq: string[] = [];
    for (const id of input.instructorIds) {
      if (!uniq.includes(id)) uniq.push(id);
    }
    if (input.engagementKind === "partner_instructor_only" && uniq.length > 1) {
      // Partner MVP: exactly one lead, no TA — keep first instructor only
      list = [{ instructorId: uniq[0]!, role: "lead" }];
    } else {
      list = uniq.map((instructorId, index) => ({
        instructorId,
        role: (index === 0 ? "lead" : "assist") as SessionInstructorRole,
      }));
    }
  } else {
    throw new SessionInstructorsValidationError(
      "Must select at least 1 instructor",
    );
  }

  const seen = new Set<string>();
  for (const row of list) {
    if (!row.instructorId) {
      throw new SessionInstructorsValidationError("Instructor id is required");
    }
    if (seen.has(row.instructorId)) {
      throw new SessionInstructorsValidationError(
        "Duplicate instructor on the same session",
      );
    }
    seen.add(row.instructorId);
    if (row.role !== "lead" && row.role !== "assist") {
      throw new SessionInstructorsValidationError("Invalid instructor role");
    }
  }

  const leads = list.filter((r) => r.role === "lead");
  const assists = list.filter((r) => r.role === "assist");

  if (leads.length !== 1) {
    throw new SessionInstructorsValidationError(
      "Session must have exactly 1 lead instructor",
    );
  }
  if (assists.length > 3) {
    throw new SessionInstructorsValidationError(
      "Session may have at most 3 TA instructors",
    );
  }
  if (
    input.engagementKind === "partner_instructor_only" &&
    assists.length > 0
  ) {
    throw new SessionInstructorsValidationError(
      "Partner sessions cannot have TA instructors",
    );
  }

  return list;
}

export function instructorIdsFromAssignments(
  rows: SessionInstructorAssignment[],
): string[] {
  return rows.map((r) => r.instructorId);
}

export type ClassSessionInstructorDTO = {
  instructorId: string;
  name: string;
  role: SessionInstructorRole;
};

/** Build response instructors (+ legacy id/name arrays) from join rows */
export function buildInstructorsDto(
  rows: Array<{
    instructorId: string | null;
    instructorName: string | null;
    role?: string | null;
  }>,
): {
  instructors: ClassSessionInstructorDTO[];
  instructorIds: string[];
  instructorNames: string[];
} {
  const instructors: ClassSessionInstructorDTO[] = [];
  for (const r of rows) {
    if (!r.instructorId) continue;
    instructors.push({
      instructorId: r.instructorId,
      name: r.instructorName ?? "",
      role: r.role === "assist" ? "assist" : "lead",
    });
  }
  return {
    instructors,
    instructorIds: instructors.map((i) => i.instructorId),
    instructorNames: instructors.map((i) => i.name),
  };
}
